<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Bell } from 'lucide-vue-next';
import { api } from '@/lib/http';
import { timeAgo } from '@/lib/format';
import { useAppStore } from '@/stores/app';

interface Notification {
  id: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

const app = useAppStore();
const router = useRouter();
const open = ref(false);
const items = ref<Notification[]>([]);
const loading = ref(false);
const root = ref<HTMLElement | null>(null);

async function toggle() {
  open.value = !open.value;
  if (open.value) {
    loading.value = true;
    try {
      const res = await api.get<{ items: Notification[]; unread: number }>('/notifications', { params: { pageSize: 8 } });
      items.value = res.items;
      app.unreadNotifications = res.unread;
    } finally {
      loading.value = false;
    }
  }
}

async function openItem(n: Notification) {
  open.value = false;
  if (!n.readAt) {
    await api.post(`/notifications/${n.id}/read`);
    void app.refreshUnread();
  }
  if (n.link) void router.push(n.link);
}

async function markAll() {
  await api.post('/notifications/read-all');
  items.value = items.value.map((i) => ({ ...i, readAt: i.readAt ?? new Date().toISOString() }));
  app.unreadNotifications = 0;
}

function onDoc(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) open.value = false;
}
onMounted(() => document.addEventListener('click', onDoc));
onBeforeUnmount(() => document.removeEventListener('click', onDoc));
</script>

<template>
  <div ref="root" class="relative">
    <button type="button" class="relative rounded-lg p-2 text-muted hover:bg-slate-100 hover:text-current dark:hover:bg-white/10" aria-label="Notifications" :aria-expanded="open" @click="toggle">
      <Bell class="h-5 w-5" />
      <span v-if="app.unreadNotifications" class="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-sun-500 px-1 text-[10px] font-bold text-white">
        {{ app.unreadNotifications > 99 ? '99+' : app.unreadNotifications }}
      </span>
    </button>
    <div v-if="open" class="card absolute right-0 z-40 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden shadow-pop">
      <div class="flex items-center justify-between border-b border-base px-4 py-2.5">
        <p class="text-sm font-semibold">Notifications</p>
        <button v-if="app.unreadNotifications" type="button" class="text-xs text-navy-600 hover:underline dark:text-navy-300" @click="markAll">Mark all read</button>
      </div>
      <ul class="max-h-96 overflow-y-auto">
        <li v-if="loading" class="px-4 py-6 text-center text-sm text-muted">Loading…</li>
        <li v-else-if="!items.length" class="px-4 py-8 text-center text-sm text-muted">You're all caught up.</li>
        <li v-for="n in items" v-else :key="n.id">
          <button type="button" class="flex w-full gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-white/5" @click="openItem(n)">
            <span class="mt-1.5 h-2 w-2 shrink-0 rounded-full" :class="n.readAt ? 'bg-transparent' : 'bg-sun-500'" />
            <span class="min-w-0">
              <span class="block truncate text-sm font-medium">{{ n.title }}</span>
              <span v-if="n.body" class="block truncate text-xs text-muted">{{ n.body }}</span>
              <span class="block text-[11px] text-muted">{{ timeAgo(n.createdAt) }}</span>
            </span>
          </button>
        </li>
      </ul>
      <RouterLink to="/notifications" class="block border-t border-base px-4 py-2.5 text-center text-xs font-medium text-navy-600 hover:bg-slate-50 dark:text-navy-300 dark:hover:bg-white/5" @click="open = false">
        View all
      </RouterLink>
    </div>
  </div>
</template>
